import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Button } from '@mui/material';
import { colors, gradients, shadows, radii } from '../../theme/designTokens';
import { withBrand } from '../BrandName';

const MagneticButton = ({
  children,
  variant = 'primary',
  sx = {},
  onClick,
  type = 'button',
  disabled = false,
  fullWidth = false,
  ...props
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * 0.15);
    y.set((e.clientY - cy) * 0.15);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  const label = typeof children === 'string' ? withBrand(children) : children;

  const variants = {
    primary: {
      background: colors.white,
      color: colors.navyDark,
      boxShadow: shadows.md,
      '&:hover': { background: colors.silverLight, boxShadow: shadows.lg },
    },
    navy: {
      background: colors.navy,
      color: colors.white,
      boxShadow: shadows.sm,
      '&:hover': { background: colors.navyLight, boxShadow: shadows.md },
    },
    outline: {
      background: 'transparent',
      color: colors.white,
      border: `1px solid ${colors.silver}`,
      boxShadow: 'none',
      '&:hover': {
        borderColor: colors.white,
        color: colors.white,
        background: 'rgba(255,255,255,0.06)',
      },
    },
    ghost: {
      background: 'transparent',
      color: colors.navy,
      border: `1px solid ${colors.navy}`,
      boxShadow: 'none',
      '&:hover': { background: 'rgba(15,23,42,0.04)' },
    },
    silver: {
      background: gradients.silver,
      color: colors.navyDark,
      boxShadow: shadows.silver,
      '&:hover': { boxShadow: shadows.lg },
    },
    gold: {
      background: gradients.gold,
      color: colors.navyDark,
      boxShadow: shadows.gold,
      borderRadius: radii.pill,
      '&:hover': {
        background: colors.goldLight,
        boxShadow: '0 8px 32px rgba(238,186,43,0.45)',
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      style={{ x: springX, y: springY, display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : 'auto' }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileTap={{ scale: 0.97 }}
    >
      <Button
        type={type}
        onClick={onClick}
        disabled={disabled}
        fullWidth={fullWidth}
        component={motion.button}
        whileHover={{ scale: 1.02 }}
        sx={{
          px: 3.5,
          py: 1.4,
          fontWeight: 600,
          fontSize: '0.9rem',
          letterSpacing: '0.06em',
          borderRadius: radii.sm,
          textTransform: 'none',
          transition: 'all 0.3s ease',
          ...(variant === 'gold' ? { borderRadius: radii.pill } : {}),
          ...variants[variant],
          ...sx,
        }}
        {...props}
      >
        {label}
      </Button>
    </motion.div>
  );
};

export default MagneticButton;
